import { useEffect } from 'react'
import { Badge, Button, Tab, Nav, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router'
import { snakeCase } from 'lodash'
import * as Yup from 'yup'
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { showSetting, selectSetting, createSettingDetail, modifySettingDetail, deleteSettingDetail } from '../media'
import { ButtonModals, CustomForm, ErrorModal, selectCommon } from '../common'
import { useAuth, shouldShowExperimentalFeatures } from '../auth'
import {
  openModal,
  closeModal,
  dismissError,
  selectId,
  unselectId,
  clearSelectedIds,
  selectTab
} from './settingActions'
import { exportSetting } from './settingAPI'
import {
  ColumnSettingForm,
  MarginOutputSettingForm,
  VerticalHeadingMarginSettingForm,
  CoveringMarginSettingForm,
  HorizontalHeadingMarginSettingForm,
  ParagraphStyleSettingForm,
  HeadingObjectStyleSettingForm,
  CharacterStyleSettingForm,
  CharacterStyleSettingDeleteForm,
  CharacterStyleSettingUpsertForm,
  PageMarginSettingForm,
  PageMarginSettingUpsertForm,
  PageMarginSettingDeleteForm,
  PageMarginOtherSettingForm,
  PageMarginOtherSettingUpsertForm,
  PageMarginOtherSettingDeleteForm,
  PreambleCharacterSettingForm,
  PreambleRowMarginSettingForm,
  FigureRowMarginSettingForm,
  HalfSizeImageCharacterSettingForm,
  LineRowMarginSettingForm,
  AuxiliaryLineSettingForm,
  ArticleRuleSettingForm,
  HeadingCombinationSettingForm
} from './settingForms'
import { halfSizeImageCharacterSettingSchema } from '../article/validateLayoutable'

import moment from 'moment'

type SettingDetail = {
  id: number
  name: string
  media: { name: string }
  pageMarginOtherSettings?: Array<{ order: number }>
  pageMarginSettings?: Array<{ pageMarginSettingType: string; order: number }>
  characterStyleSettings?: unknown[]
  columnSetting?: { numberOfCharactersPerRow?: number }
  halfSizeImageCharacterSetting?: unknown
  [key: string]: unknown
}

type EnumValues = {
  pageMarginSettingTypes: Record<string, string>
  [key: string]: Record<string, string>
}

const halfSizeImageCharacterSettingValidator = (setting: SettingDetail | null) => {
  const halfSizeImageCharacterSetting = setting?.halfSizeImageCharacterSetting
  const validateObject = {
    numberOfCharactersPerRow: setting?.columnSetting?.numberOfCharactersPerRow,
    ...(halfSizeImageCharacterSetting as Record<string, unknown>)
  }
  try {
    halfSizeImageCharacterSettingSchema.validateSync(validateObject)
  } catch (error) {
    if (error instanceof Yup.ValidationError) {
      return error.message
    } else {
      throw error
    }
  }
}

const getTabConfigList = ({
  currentUser,
  dispatch,
  enumValues,
  isSettingWritable,
  selectedIds,
  setting,
  alert
}: {
  currentUser: { role?: string } | null | undefined
  dispatch: (action: unknown) => void
  enumValues: EnumValues
  isSettingWritable: boolean
  selectedIds: number[]
  setting: SettingDetail
  alert: unknown
}) => {
  const handleCreateCharacterStyleSettingSubmit = ({ ...props }: Record<string, unknown>) => {
    const settingName = snakeCase('characterStyleSettings')
    const settingId = setting.id
    dispatch(createSettingDetail({ settingId, settingName, ...props }))
  }
  const handleDeleteCharacterStyleSettingSubmit = ({ selectedIds }: { selectedIds: number[] }) => {
    const settingName = snakeCase('characterStyleSettings')
    const settingId = setting.id
    dispatch(deleteSettingDetail({ settingId, settingName, id: selectedIds[0] }))
  }
  const handleCreatePageMarginSettingSubmit = ({ ...props }: Record<string, unknown>) => {
    const settingName = snakeCase('pageMarginSettings')
    const settingId = setting.id
    const pageMarginSetting = { ...props }
    dispatch(createSettingDetail({ settingId, settingName, ...pageMarginSetting }))
  }
  const handleDeletePageMarginSettingSubmit = ({ selectedIds }: { selectedIds: number[] }) => {
    const settingName = snakeCase('pageMarginSettings')
    const settingId = setting.id
    dispatch(deleteSettingDetail({ settingId, settingName, id: selectedIds[0] }))
  }
  const handleCreatePageMarginOtherSettingSubmit = ({ ...props }: Record<string, unknown>) => {
    const settingName = snakeCase('pageMarginOtherSettings')
    const settingId = setting.id
    const pageMarginOtherSetting = { ...props }
    dispatch(createSettingDetail({ settingId, settingName, ...pageMarginOtherSetting }))
  }
  const handleDeletePageMarginOtherSettingSubmit = ({ selectedIds }: { selectedIds: number[] }) => {
    const settingName = snakeCase('pageMarginOtherSettings')
    const settingId = setting.id
    dispatch(deleteSettingDetail({ settingId, settingName, id: selectedIds[0] }))
  }
  const largestPageMarginOtherOrder =
    setting?.pageMarginOtherSettings
      ?.slice()
      .sort((a, b) => b.order - a.order)
      ?.map((s) => s.order)
      ?.at(0) ?? -1
  const largestOrders = Object.keys(enumValues.pageMarginSettingTypes).reduce((acc: Record<string, number>, type) => {
    const largestOrder =
      setting?.pageMarginSettings
        ?.filter((s) => s.pageMarginSettingType === type)
        ?.sort((a, b) => b.order - a.order)
        ?.map((s) => s.order)
        ?.at(0) ?? -1
    acc[type] = largestOrder
    return acc
  }, {})
  const initType = 'region'
  const initOrder = largestOrders[initType] + 1
  return [
    { title: '組版体裁', key: 'columnSetting', form: ColumnSettingForm, schema: ColumnSettingForm.schema },
    {
      title: '欄外出力設定',
      key: 'marginOutputSetting',
      form: MarginOutputSettingForm,
      schema: MarginOutputSettingForm.schema
    },
    {
      title: '欄外項目',
      key: 'pageMarginSettings',
      isExperimental: true,
      isArray: true,
      form: PageMarginSettingForm,
      schema: PageMarginSettingForm.schema,
      buttonModalSettings: [
        {
          name: 'createPageMargin',
          button: {
            handleClick: () => dispatch(openModal('createPageMargin')),
            disabled: currentUser?.role !== 'group_administrator' || !isSettingWritable,
            icon: 'plus',
            text: '新規'
          },
          form: {
            initialValues: {
              pageMarginSettingType: initType,
              pageMarginOtherSettingId: '',
              order: initOrder,
              name: ''
            },
            handleSubmit: handleCreatePageMarginSettingSubmit,
            Component: PageMarginSettingUpsertForm,
            validationSchema: PageMarginSettingUpsertForm.schema,
            props: {
              title: '欄外項目追加',
              onClickCancel: () => dispatch(closeModal()),
              submitButtonText: '追加する',
              enumValues,
              isSettingWritable,
              alert,
              largestOrders,
              setting
            }
          }
        },
        {
          name: 'deletePageMarginSetting',
          button: {
            handleClick: () => dispatch(openModal('deletePageMarginSetting')),
            disabled: currentUser?.role !== 'group_administrator' || !isSettingWritable || selectedIds.length !== 1,
            icon: 'trash',
            variant: 'outline-danger',
            text: '削除'
          },
          form: {
            initialValues: { selectedIds },
            handleSubmit: handleDeletePageMarginSettingSubmit,
            Component: PageMarginSettingDeleteForm,
            props: {
              title: '欄外項目削除',
              onClickCancel: () => dispatch(closeModal()),
              submitButtonText: '削除する',
              selectedIds,
              pageMarginSettings: setting?.pageMarginSettings,
              enumValues,
              isSettingWritable
            }
          }
        }
      ]
    },
    {
      title: '欄外項目・その他項目名',
      key: 'pageMarginOtherSettings',
      isArray: true,
      isExperimental: true,
      form: PageMarginOtherSettingForm,
      schema: PageMarginOtherSettingForm.schema,
      buttonModalSettings: [
        {
          name: 'createPageMarginOtherSetting',
          button: {
            handleClick: () => dispatch(openModal('createPageMarginOtherSetting')),
            disabled: currentUser?.role !== 'group_administrator' || !isSettingWritable,
            icon: 'plus',
            text: '新規'
          },
          form: {
            initialValues: { order: largestPageMarginOtherOrder + 1, name: '' },
            handleSubmit: handleCreatePageMarginOtherSettingSubmit,
            Component: PageMarginOtherSettingUpsertForm,
            validationSchema: PageMarginOtherSettingUpsertForm.schema,
            props: {
              title: '欄外項目・その他項目名追加',
              onClickCancel: () => dispatch(closeModal()),
              submitButtonText: '追加する',
              enumValues,
              isSettingWritable,
              alert
            }
          }
        },
        {
          name: 'deletePageMarginOtherSetting',
          button: {
            handleClick: () => dispatch(openModal('deletePageMarginOtherSetting')),
            disabled: currentUser?.role !== 'group_administrator' || !isSettingWritable || selectedIds.length !== 1,
            icon: 'trash',
            variant: 'outline-danger',
            text: '削除'
          },
          form: {
            initialValues: { selectedIds },
            handleSubmit: handleDeletePageMarginOtherSettingSubmit,
            Component: PageMarginOtherSettingDeleteForm,
            props: {
              title: '欄外項目・その他項目名削除',
              onClickCancel: () => dispatch(closeModal()),
              submitButtonText: '削除する',
              selectedIds,
              pageMarginSettings: setting?.pageMarginSettings,
              enumValues,
              isSettingWritable
            }
          }
        }
      ]
    },
    {
      title: '縦見出し行取り',
      key: 'verticalHeadingMarginSettings',
      isArray: true,
      form: VerticalHeadingMarginSettingForm,
      schema: VerticalHeadingMarginSettingForm.schema
    },
    {
      title: '被せ字取り',
      key: 'coveringMarginSettings',
      isArray: true,
      form: CoveringMarginSettingForm,
      schema: CoveringMarginSettingForm.schema
    },
    {
      title: '横見出し行取り',
      key: 'horizontalHeadingMarginSettings',
      isArray: true,
      form: HorizontalHeadingMarginSettingForm,
      schema: HorizontalHeadingMarginSettingForm.schema
    },
    {
      title: '段落スタイル',
      key: 'paragraphStyleSettings',
      isArray: true,
      form: ParagraphStyleSettingForm,
      schema: ParagraphStyleSettingForm.schema
    },
    {
      title: '肩掛け見出しスタイル',
      key: 'headingCombinationSettings',
      isExperimental: true,
      form: HeadingCombinationSettingForm,
      schema: HeadingCombinationSettingForm.schema
    },
    {
      title: '見出し上下の空き',
      key: 'headingObjectStyleSettings',
      isArray: true,
      form: HeadingObjectStyleSettingForm,
      schema: HeadingObjectStyleSettingForm.schema
    },
    {
      title: '文字スタイル',
      key: 'characterStyleSettings',
      isArray: true,
      form: CharacterStyleSettingForm,
      schema: CharacterStyleSettingForm.schema,
      buttonModalSettings: [
        {
          name: 'createCharacterStyle',
          button: {
            handleClick: () => dispatch(openModal('createCharacterStyle')),
            disabled: currentUser?.role !== 'group_administrator' || !isSettingWritable,
            icon: 'plus',
            text: '新規'
          },
          form: {
            initialValues: { characterStyleSettingType: 'sentence', description: '', name: '' },
            handleSubmit: handleCreateCharacterStyleSettingSubmit,
            Component: CharacterStyleSettingUpsertForm,
            validationSchema: CharacterStyleSettingUpsertForm.schema,
            props: {
              title: '文字スタイル追加',
              onClickCancel: () => dispatch(closeModal()),
              submitButtonText: '追加する',
              enumValues,
              isSettingWritable,
              alert
            }
          }
        },
        {
          name: 'deleteCharacterStyle',
          button: {
            handleClick: () => dispatch(openModal('deleteCharacterStyle')),
            disabled: currentUser?.role !== 'group_administrator' || !isSettingWritable || selectedIds.length !== 1,
            icon: 'trash',
            variant: 'outline-danger',
            text: '削除'
          },
          form: {
            initialValues: { selectedIds },
            handleSubmit: handleDeleteCharacterStyleSettingSubmit,
            Component: CharacterStyleSettingDeleteForm,
            props: {
              title: '文字スタイル削除',
              onClickCancel: () => dispatch(closeModal()),
              submitButtonText: '削除する',
              selectedIds,
              characterStyleSettings: setting?.characterStyleSettings,
              enumValues,
              isSettingWritable
            }
          }
        }
      ]
    },
    {
      title: '前文字組',
      key: 'preambleCharacterSettings',
      isArray: true,
      form: PreambleCharacterSettingForm,
      schema: PreambleCharacterSettingForm.schema
    },
    {
      title: '前文行取り',
      key: 'preambleRowMarginSettings',
      isArray: true,
      form: PreambleRowMarginSettingForm,
      schema: PreambleRowMarginSettingForm.schema
    },
    {
      title: '写真基本行取り',
      key: 'figureRowMarginSettings',
      isArray: true,
      form: FigureRowMarginSettingForm,
      schema: FigureRowMarginSettingForm.schema
    },
    {
      title: '画像半指定時の字数設定',
      key: 'halfSizeImageCharacterSetting',
      isExperimental: true,
      form: HalfSizeImageCharacterSettingForm,
      schema: HalfSizeImageCharacterSettingForm.schema,
      extraInitValues: { numberOfCharactersPerRow: setting?.columnSetting?.numberOfCharactersPerRow },
      validator: halfSizeImageCharacterSettingValidator,
      validateOnMount: true
    },
    {
      title: '仕切り罫線・ヤマ罫線',
      key: 'lineRowMarginSettings',
      isArray: true,
      form: LineRowMarginSettingForm,
      schema: LineRowMarginSettingForm.schema
    },
    {
      title: '補助線',
      key: 'auxiliaryLineSettings',
      isArray: true,
      form: AuxiliaryLineSettingForm,
      schema: AuxiliaryLineSettingForm.schema
    },
    { title: '組版ルール', key: 'articleRules', isExperimental: true, form: ArticleRuleSettingForm }
  ]
}

const getRightPane =
  ({
    title,
    key,
    isArray,
    form: ComponentForm,
    schema,
    buttonModalSettings,
    extraInitValues,
    validateOnMount
  }: {
    title: string
    key: string
    isArray?: boolean
    form: React.ComponentType<any>
    schema?: object
    buttonModalSettings?: any[]
    extraInitValues?: Record<string, unknown>
    validateOnMount?: boolean
  }) =>
  ({ setting, errorMessage, ...props }: { setting: SettingDetail; errorMessage?: string; [key: string]: any }) => {
    const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>()
    const getHandleSettingDetailSubmit =
      (settingName: string, isArray?: boolean) =>
      ({ ...props }: Record<string, unknown>) => {
        let snakeSettingName = snakeCase(settingName)
        if (!isArray) {
          snakeSettingName = `${snakeSettingName}s`
        }
        dispatch(modifySettingDetail({ settingId: setting.id, settingName: snakeSettingName, ...props } as any))
      }
    const { openedModal } = props
    const settingDetail = setting[key]
    if (!ComponentForm) {
      return (
        <Tab.Pane key={`tab-pane-${key}`} eventKey={key}>
          <h3 key={`title-${key}`} className='mb-4'>
            {title}
          </h3>
          未実装
        </Tab.Pane>
      )
    }
    const initialTouched =
      errorMessage && settingDetail && typeof settingDetail === 'object' && !Array.isArray(settingDetail)
        ? Object.keys(settingDetail as Record<string, unknown>).reduce(
            (acc: Record<string, boolean>, k) => (k !== 'id' ? { ...acc, [k]: true } : acc),
            {}
          )
        : {}
    if (isArray) {
      return (
        <Tab.Pane key={`tab-pane-${key}`} eventKey={key}>
          <h3 key={`title-${key}`} className='mb-4'>
            {title}
          </h3>
          {!!settingDetail &&
            ComponentForm &&
            (settingDetail as any[]).map((val: any, rowIndex: number) => {
              const formSetting = {
                initialValues: { ...val },
                initialTouched,
                validateOnMount,
                validationSchema: schema,
                Component: ComponentForm,
                handleSubmit: getHandleSettingDetailSubmit(key, isArray),
                props: { dispatch, rowIndex, setting, errorMessage, ...props }
              }
              const componentKey = `${key}-${val.id}`
              return <CustomForm key={componentKey} form={formSetting} {...props} />
            })}
          {buttonModalSettings && (
            <>
              <br />
              <ButtonModals settings={buttonModalSettings} openedModal={openedModal} />
            </>
          )}
        </Tab.Pane>
      )
    } else {
      return (
        <Tab.Pane key={`tab-pane-${key}`} eventKey={key}>
          <h3 key={`title-${key}`} className='mb-4'>
            {title}
          </h3>
          {!!settingDetail && ComponentForm && (
            <CustomForm
              form={{
                initialValues: { ...(settingDetail as Record<string, unknown>), ...extraInitValues },
                initialTouched,
                validateOnMount,
                validationSchema: schema,
                handleSubmit: getHandleSettingDetailSubmit(key, isArray),
                Component: ComponentForm,
                props: { dispatch, setting, errorMessage, ...props }
              }}
              {...props}
            />
          )}
        </Tab.Pane>
      )
    }
  }

const SettingConfigDetails = ({
  setting,
  settingId,
  fetching,
  currentTab,
  dispatch,
  isSettingExportable,
  canShowExperimentalFeatures,
  ...props
}: {
  setting: SettingDetail | null | undefined
  settingId: string | undefined
  fetching: boolean
  currentTab: string
  dispatch: (action: unknown) => void
  isSettingExportable: boolean
  canShowExperimentalFeatures: boolean
  [key: string]: any
}) => {
  if (fetching || !setting) {
    return
  }
  const tabConfigList = getTabConfigList({ dispatch, setting, ...(props as any) })
  const exportClickHandler = () => {
    const exportDateTime = moment(new Date()).format('YYYYMMDD_hhmm')
    const fileName = `${setting.media.name}_${setting.name}_${exportDateTime}.zip`
    dispatch(exportSetting({ id: settingId ?? '', fileName }))
  }
  const validationResults = tabConfigList.reduce((acc: Record<string, string | undefined>, config) => {
    const { isExperimental, key, validator } = config
    if (!isExperimental || canShowExperimentalFeatures) {
      const errorMessage = validator !== undefined ? validator(setting) : undefined
      acc[key] = errorMessage
    }
    return acc
  }, {})
  const leftNavs = tabConfigList.map(({ title, key, isExperimental }) => {
    if (isExperimental && !canShowExperimentalFeatures) {
      return null
    }
    const errorMessage = validationResults?.[key]
    const errorNotify = errorMessage ? (
      <>
        <OverlayTrigger
          placement='auto'
          overlay={(tooltipProps) => <Tooltip {...tooltipProps}>{errorMessage}</Tooltip>}
        >
          <Badge bg='danger' pill>
            !
          </Badge>
        </OverlayTrigger>
      </>
    ) : null
    return (
      <Nav.Item key={`tab-nav-${key}`}>
        <Nav.Link eventKey={key}>
          {title}
          {errorNotify}
        </Nav.Link>
      </Nav.Item>
    )
  })
  const rightPanes = tabConfigList.map((c) => {
    if (c.isExperimental && !canShowExperimentalFeatures) {
      return null
    }
    const errorMessage = validationResults?.[c.key]
    const RightPane = getRightPane(c)
    return <RightPane key={c.key} setting={setting} errorMessage={errorMessage} {...props} />
  })
  return (
    <Tab.Container
      id={`setting-tab-${settingId}`}
      defaultActiveKey={currentTab}
      onSelect={(e) => {
        dispatch(selectTab(e ?? ''))
        if (props?.selectedIds?.length > 0) {
          dispatch(clearSelectedIds())
        }
      }}
    >
      <Row className='align-items-center'>
        <Col className='mr-auto'>
          <h2 className='m-2 settingName'>紙面ベース設定: {setting.name}</h2>
        </Col>
        {isSettingExportable && (
          <Col sm='auto' className='justify-content-end m-3'>
            <Button
              className='export-button'
              variant='outline-primary'
              size='sm'
              disabled={!isSettingExportable}
              onClick={exportClickHandler}
            >
              <FontAwesomeIcon icon='file-export' />
              エクスポート
            </Button>
          </Col>
        )}
      </Row>
      <Row className='me-2'>
        <Col sm={2} className='fs-5'>
          <Nav variant='pills' className='flex-column'>
            {leftNavs.map((leftNav) => leftNav)}
          </Nav>
        </Col>
        <Col sm={10}>
          <Tab.Content>{rightPanes.map((rightPane) => rightPane)}</Tab.Content>
        </Col>
      </Row>
    </Tab.Container>
  )
}

export const SettingConfig = () => {
  const { fetching, currentTab, openedModal, selectedIds, setting, alert, errors } = useSelector(selectSetting)
  const { enumValues } = useSelector(selectCommon) as unknown as { enumValues: EnumValues }
  const auth = useAuth()
  const { currentUser } = auth
  const isSettingWritable = currentUser?.role === 'group_administrator'
  const canShowExperimentalFeatures = shouldShowExperimentalFeatures(auth)
  const isSettingExportable = canShowExperimentalFeatures || currentUser?.role === 'group_administrator'
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>()
  const { settingId } = useParams()
  useEffect(() => {
    dispatch(showSetting({ settingId: settingId ?? '' }))
  }, [dispatch, settingId])
  return (
    <>
      <ErrorModal errors={errors} onClose={(errorKey) => dispatch(dismissError(errorKey))} />
      <SettingConfigDetails
        setting={setting as SettingDetail | null}
        settingId={settingId}
        {...{
          dispatch,
          fetching,
          currentUser,
          enumValues,
          isSettingWritable,
          isSettingExportable,
          canShowExperimentalFeatures,
          currentTab,
          openedModal,
          selectedIds,
          selectId,
          unselectId,
          alert
        }}
      />
    </>
  )
}
